/**
 * Custom hook that owns all Drag and Drop state and logic for the Trip page.
 *
 * Extracted from Trip.tsx to keep that component focused on layout/rendering.
 *
 * Handles sensors, collision detection, drag start/end for every draggable type
 * (CARD, LIST, DIVIDER, EVENT), optimistic Redux updates, and the confirm-drop
 * flow for duplicate or closed-venue drops.
 */

import {
  type Active,
  type CollisionDetection,
  closestCenter,
  type DragEndEvent,
  type DragStartEvent,
  KeyboardSensor,
  PointerSensor,
  pointerWithin,
  useSensor,
  useSensors,
} from '@dnd-kit/core';
import { arrayMove, sortableKeyboardCoordinates } from '@dnd-kit/sortable';
import { Button } from '@mui/material';
import { type SnackbarKey, useSnackbar } from 'notistack';
import { useCallback, useState } from 'react';
import { useAppDispatch, useAppSelector } from '../../../redux/hooks';
import { selectCurrentTrip } from '../../../redux/trip/trip.selectors';
import {
  addEventToDate,
  deleteEventFromDate,
  moveCardToList,
  moveEventToDate,
  saveCardOrderForActiveList,
  saveDividerOrder,
  saveEventOrderForDay,
  saveListBetweenDividers,
  saveListOrder,
  saveListOutOfDivider,
  saveListToDivider,
  tempUpdateCardOrder,
  tempUpdateDividerListOrder,
  tempUpdateEventOrder,
  tempUpdateListOrder,
} from '../../../redux/trip/trip.slice';
import type { TripCard, TripDay, TripEvent } from '../../../redux/trip/trip.types';
import { ItemType } from '../../../types/DragAndDrop';
import { isOpenOnDate } from '../../../utils/Card';
import type { PendingDrop } from './types';

export interface UseTripDndHandlersResult {
  sensors: ReturnType<typeof useSensors>;
  collisionDetectionStrategy: CollisionDetection;
  activeItem: Active | null;
  pendingDrop: PendingDrop | null;
  handleDragStart: (event: DragStartEvent) => void;
  handleDragEnd: (event: DragEndEvent) => void;
  handleConfirmDrop: () => void;
  handleCancelDrop: () => void;
}

/**
 * Encapsulates all dnd-kit drag-and-drop state, sensors, collision detection,
 * and drag-end dispatch logic for the Trip page.
 *
 * @param tripId - The current trip ID from route params.
 */
export function useTripDndHandlers(tripId: string | undefined): UseTripDndHandlersResult {
  const dispatch = useAppDispatch();
  const currentTrip = useAppSelector(selectCurrentTrip);
  const { enqueueSnackbar, closeSnackbar } = useSnackbar();

  const [activeItem, setActiveItem] = useState<Active | null>(null);
  const [pendingDrop, setPendingDrop] = useState<PendingDrop | null>(null);

  // ── Sensors ───────────────────────────────────────────────────────────────
  // PointerSensor with a distance threshold prevents accidental drags on click.
  // KeyboardSensor enables accessible keyboard-driven reordering.
  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 8 } }),
    useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates })
  );

  // ── Collision detection ───────────────────────────────────────────────────
  // pointerWithin finds the most specific droppable under the cursor (e.g. a
  // card inside a list). Fall back to closestCenter so sortable items that
  // don't contain the pointer can still be targeted by proximity.
  const collisionDetectionStrategy: CollisionDetection = useCallback((args) => {
    const pointerCollisions = pointerWithin(args);
    return pointerCollisions.length > 0 ? pointerCollisions : closestCenter(args);
  }, []);

  const handleDragStart = useCallback(({ active }: DragStartEvent) => setActiveItem(active), []);

  const handleDragEnd = useCallback(
    ({ active, over }: DragEndEvent) => {
      setActiveItem(null);
      if (!currentTrip || !tripId) {
        return;
      }

      // Shared helper: card being added to a day (no existing event to move)
      const handleCardDropToDay = (card: TripCard, tripDay: TripDay, dateISO: string) => {
        const dayEvents = (tripDay.eventOrder ?? [])
          .map((id: string) => currentTrip.events[id])
          .filter(Boolean);
        const alreadyOnDay = dayEvents.some((e: TripEvent) => e.cardId === card.id);
        // Duplicate is checked before closed so that a place that is both already
        // on the day AND has closed hours triggers a two-step confirmation flow.
        // handleConfirmDrop re-checks for closed after the user confirms duplicate.
        if (alreadyOnDay) {
          setPendingDrop({ type: 'duplicate', card, dateISO });
        } else if (!isOpenOnDate(card, tripDay)) {
          setPendingDrop({ type: 'closed', card, dateISO });
        } else {
          dispatch(
            addEventToDate({
              tripId,
              dayId: tripDay.id,
              dateISO,
              cardId: card.id,
              eventOrder: tripDay.eventOrder,
            })
          );
        }
      };

      // Shared helper: existing event being moved to a different day
      const handleEventMoveToDay = (
        card: TripCard,
        event: TripEvent,
        eventId: string,
        tripDay: TripDay,
        toDateISO: string
      ) => {
        const dayEvents = (tripDay.eventOrder ?? [])
          .map((id: string) => currentTrip.events[id])
          .filter(Boolean);
        // Exclude the event being moved when checking for duplicates. An event
        // should never exist on two days simultaneously, but this prevents it
        // from counting itself as a duplicate if the data is ever inconsistent.
        const alreadyOnDay = dayEvents.some(
          (e: TripEvent) => e.id !== eventId && e.cardId === card.id
        );
        if (alreadyOnDay) {
          setPendingDrop({ type: 'duplicate', card, event, dateISO: toDateISO });
        } else if (!isOpenOnDate(card, tripDay)) {
          setPendingDrop({ type: 'closed', card, event, dateISO: toDateISO });
        } else {
          const fromDay = currentTrip.days[event.startDateISO];
          if (fromDay?.id && fromDay.eventOrder) {
            dispatch(
              moveEventToDate({
                tripId,
                fromDayId: fromDay.id,
                toDayId: tripDay.id,
                fromDateISO: event.startDateISO,
                toDateISO,
                eventId,
                fromDayEventOrder: fromDay.eventOrder,
                toDayEventOrder: tripDay.eventOrder,
              })
            );
          }
        }
      };

      const activeType = active.data.current?.type as string | undefined;
      const overType = over?.data.current?.type as string | undefined;

      // ── CARD ───────────────────────────────────────────────────────────────
      if (activeType === ItemType.CARD) {
        if (!over) {
          return;
        }
        const activeCard = active.data.current?.card as TripCard;
        const activeListId = active.data.current?.listId as string;

        if (overType === ItemType.CARD) {
          const overListId = over.data.current?.listId as string;
          if (activeListId === overListId && active.id !== over.id) {
            // Within-list reorder
            const list = currentTrip.lists.find((l) => l.id === activeListId);
            const origOrder = list?.cardOrder ?? [];
            const oldIndex = origOrder.indexOf(active.id as string);
            const newIndex = origOrder.indexOf(over.id as string);
            if (oldIndex !== -1 && newIndex !== -1 && oldIndex !== newIndex) {
              const newOrder = arrayMove(origOrder, oldIndex, newIndex);
              dispatch(tempUpdateCardOrder({ dragIndex: oldIndex, hoverIndex: newIndex }));
              dispatch(
                saveCardOrderForActiveList({
                  tripId,
                  listId: activeListId,
                  cardOrder: newOrder,
                  originalCardOrder: origOrder,
                })
              );
            }
          }
          return;
        }

        // overType === ItemType.LIST: card dropped on a sidebar list item (cross-list move).
        // over.data.current.listId is the target list ID (set in ListItem's useSortable data).
        if (overType === ItemType.LIST) {
          const targetListId = (over.data.current?.listId as string) ?? (over.id as string);
          if (targetListId && targetListId !== activeListId) {
            const fromList = currentTrip.lists.find((l) => l.id === activeListId);
            const toList = currentTrip.lists.find((l) => l.id === targetListId);
            if (fromList?.cardOrder && toList) {
              dispatch(
                moveCardToList({
                  tripId,
                  cardId: active.id as string,
                  fromListId: activeListId,
                  toListId: targetListId,
                  fromListCardOrder: fromList.cardOrder,
                  toListCardOrder: toList.cardOrder ?? [],
                })
              );
            }
          }
          return;
        }

        if (overType === 'day') {
          const dateISO = over.data.current?.dateISO as string;
          const tripDay = over.data.current?.tripDay;
          if (!tripDay || !activeCard) {
            return;
          }
          handleCardDropToDay(activeCard, tripDay, dateISO);
          return;
        }

        // Card dropped directly on an event — treat as a drop on that event's day
        if (overType === ItemType.EVENT) {
          const overEvent = over.data.current?.event as TripEvent;
          if (!overEvent?.startDateISO || !activeCard) {
            return;
          }
          const dateISO = overEvent.startDateISO;
          const tripDay = currentTrip.days[dateISO];
          if (!tripDay) {
            return;
          }
          handleCardDropToDay(activeCard, tripDay, dateISO);
          return;
        }

        return;
      }

      // ── LIST ──────────────────────────────────────────────────────────────
      if (activeType === ItemType.LIST) {
        if (!over) {
          return;
        }
        const activeListId = active.id as string;
        const activeDividerId = active.data.current?.dividerId as string | undefined;

        if (overType === ItemType.LIST) {
          // Both the sortable and the card-drop useDroppable share the same DOM node.
          // Both include listId in their data so we can always read it directly.
          const resolvedOverListId = (over.data.current?.listId as string) ?? (over.id as string);

          if (resolvedOverListId === activeListId) {
            return;
          }

          // dividerId may not be forwarded in sortable data for lists inside a divider,
          // so fall back to scanning which divider owns the target list by its listOrder.
          const overDividerId =
            (over.data.current?.dividerId as string | undefined) ??
            currentTrip.dividers?.find((d) => d.listOrder.includes(resolvedOverListId))?.id;

          if (activeDividerId && overDividerId && activeDividerId === overDividerId) {
            // Within-divider reorder
            const divider = currentTrip.dividers?.find((d) => d.id === activeDividerId);
            if (divider) {
              const origOrder = divider.listOrder;
              const oldIndex = origOrder.indexOf(activeListId);
              const newIndex = origOrder.indexOf(resolvedOverListId);
              if (oldIndex !== -1 && newIndex !== -1 && oldIndex !== newIndex) {
                const newOrder = arrayMove(origOrder, oldIndex, newIndex);
                dispatch(
                  tempUpdateDividerListOrder({
                    dividerId: activeDividerId,
                    dragIndex: oldIndex,
                    hoverIndex: newIndex,
                  })
                );
                dispatch(
                  saveDividerOrder({
                    tripId,
                    dividerId: activeDividerId,
                    listOrder: newOrder,
                    originalListOrder: origOrder,
                  })
                );
              }
            }
          } else if (overDividerId && overDividerId !== activeDividerId) {
            // Dropped on a list inside a different divider → move into that divider
            const targetDivider = currentTrip.dividers?.find((d) => d.id === overDividerId);
            if (targetDivider) {
              if (activeDividerId) {
                const sourceDivider = currentTrip.dividers?.find((d) => d.id === activeDividerId);
                dispatch(
                  saveListBetweenDividers({
                    tripId,
                    listId: activeListId,
                    fromDividerId: activeDividerId,
                    fromDividerListOrder: sourceDivider?.listOrder ?? [],
                    toDividerId: overDividerId,
                    toDividerListOrder: targetDivider.listOrder,
                    targetIndex: targetDivider.listOrder.length,
                  })
                );
              } else {
                dispatch(
                  saveListToDivider({
                    tripId,
                    listId: activeListId,
                    tripListOrder: currentTrip.listOrder,
                    targetDividerId: overDividerId,
                    targetDividerListOrder: targetDivider.listOrder,
                    targetIndex: targetDivider.listOrder.length,
                  })
                );
              }
            }
          } else if (!activeDividerId && !overDividerId) {
            // Both top-level (no divider): reorder in listOrder
            const dragIndex = currentTrip.listOrder.indexOf(activeListId);
            const hoverIndex = currentTrip.listOrder.indexOf(resolvedOverListId);
            if (dragIndex !== -1 && hoverIndex !== -1 && dragIndex !== hoverIndex) {
              const newOrder = arrayMove(currentTrip.listOrder, dragIndex, hoverIndex);
              dispatch(tempUpdateListOrder({ dragIndex, hoverIndex }));
              dispatch(
                saveListOrder({
                  tripId,
                  listOrder: newOrder,
                  originalListOrder: currentTrip.listOrder,
                })
              );
            }
          }
          return;
        }

        // LIST dropped on a divider — via the sortable node (handles collapsed dividers)
        // or the container droppable (open-divider background area). Both result in the
        // same operation: move the list into the target divider.
        if (overType === ItemType.DIVIDER || overType === 'divider-container') {
          // For the sortable node (ItemType.DIVIDER), over.id IS the divider ID.
          // For the container droppable ('divider-container'), the divider ID is
          // stored in data because the droppable's own id is a different string.
          const targetDividerId =
            overType === 'divider-container'
              ? (over.data.current?.dividerId as string)
              : (over.id as string);
          if (!targetDividerId || targetDividerId === activeDividerId) {
            return;
          }
          const targetDivider = currentTrip.dividers?.find((d) => d.id === targetDividerId);
          if (!targetDivider) {
            return;
          }
          if (activeDividerId) {
            const sourceDivider = currentTrip.dividers?.find((d) => d.id === activeDividerId);
            dispatch(
              saveListBetweenDividers({
                tripId,
                listId: activeListId,
                fromDividerId: activeDividerId,
                fromDividerListOrder: sourceDivider?.listOrder ?? [],
                toDividerId: targetDividerId,
                toDividerListOrder: targetDivider.listOrder,
                targetIndex: targetDivider.listOrder.length,
              })
            );
          } else {
            dispatch(
              saveListToDivider({
                tripId,
                listId: activeListId,
                tripListOrder: currentTrip.listOrder,
                targetDividerId,
                targetDividerListOrder: targetDivider.listOrder,
                targetIndex: targetDivider.listOrder.length,
              })
            );
          }
          return;
        }

        if (overType === 'gap') {
          const insertIndex = over.data.current?.insertIndex as number;
          if (activeDividerId) {
            // The list currently lives inside a divider, so it is absent from
            // tripListOrder. No index adjustment is needed — insertIndex is already
            // correct relative to the flat top-level order after removal.
            const divider = currentTrip.dividers?.find((d) => d.id === activeDividerId);
            dispatch(
              saveListOutOfDivider({
                tripId,
                listId: activeListId,
                dividerId: activeDividerId,
                dividerListOrder: divider?.listOrder ?? [],
                tripListOrder: currentTrip.listOrder,
                targetIndex: insertIndex,
              })
            );
          } else {
            const dragIndex = currentTrip.listOrder.indexOf(activeListId);
            if (dragIndex === -1) {
              return;
            }
            // Removing the item at dragIndex shifts every subsequent index down by 1.
            // If dragIndex < insertIndex the effective insertion point is one less.
            const adjustedTarget = dragIndex < insertIndex ? insertIndex - 1 : insertIndex;
            if (dragIndex === adjustedTarget) {
              return;
            }
            const newOrder = [...currentTrip.listOrder];
            newOrder.splice(dragIndex, 1);
            newOrder.splice(adjustedTarget, 0, activeListId);
            dispatch(tempUpdateListOrder({ dragIndex, hoverIndex: adjustedTarget }));
            dispatch(
              saveListOrder({
                tripId,
                listOrder: newOrder,
                originalListOrder: currentTrip.listOrder,
              })
            );
          }
        }
        return;
      }

      // ── DIVIDER ──────────────────────────────────────────────────────────
      if (activeType === ItemType.DIVIDER) {
        if (!over) {
          return;
        }
        const activeDividerId = active.id as string;

        if (
          (overType === 'divider-header' || overType === ItemType.DIVIDER) &&
          active.id !== over.id
        ) {
          // Reorder dividers using arrayMove on the top-level listOrder
          const dragIndex = currentTrip.listOrder.indexOf(activeDividerId);
          const hoverIndex = currentTrip.listOrder.indexOf(over.id as string);
          if (dragIndex === -1 || hoverIndex === -1 || dragIndex === hoverIndex) {
            return;
          }
          const newOrder = arrayMove(currentTrip.listOrder, dragIndex, hoverIndex);
          dispatch(tempUpdateListOrder({ dragIndex, hoverIndex }));
          dispatch(
            saveListOrder({
              tripId,
              listOrder: newOrder,
              originalListOrder: currentTrip.listOrder,
            })
          );
          return;
        }

        if (overType === 'gap') {
          const insertIndex = over.data.current?.insertIndex as number;
          const dragIndex = currentTrip.listOrder.indexOf(activeDividerId);
          if (dragIndex === -1) {
            return;
          }
          // Same index-shift compensation as the LIST gap case above.
          const adjustedTarget = dragIndex < insertIndex ? insertIndex - 1 : insertIndex;
          if (dragIndex === adjustedTarget) {
            return;
          }
          const newOrder = [...currentTrip.listOrder];
          newOrder.splice(dragIndex, 1);
          newOrder.splice(adjustedTarget, 0, activeDividerId);
          dispatch(tempUpdateListOrder({ dragIndex, hoverIndex: adjustedTarget }));
          dispatch(
            saveListOrder({
              tripId,
              listOrder: newOrder,
              originalListOrder: currentTrip.listOrder,
            })
          );
        }
        return;
      }

      // ── EVENT ─────────────────────────────────────────────────────────────
      if (activeType === ItemType.EVENT) {
        const activeEvent = active.data.current?.event as TripEvent;
        const activeCard = active.data.current?.card as TripCard;
        if (!activeEvent?.id) {
          return;
        }
        const activeEventId = activeEvent.id; // narrowed to string after guard

        // Removes the event from its day and shows a snackbar so the user knows
        // the card is back in their list. Used for both off-canvas drops and
        // sidebar drops.
        const removeEventAndNotify = () => {
          const day = currentTrip.days[activeEvent.startDateISO];
          if (!day?.id || !day.eventOrder) {
            return;
          }
          const list = currentTrip.lists.find(
            (l) => l.id === currentTrip.cardIdToListId?.[activeCard.id]
          );
          const action = (snackbarId: SnackbarKey) => (
            <Button
              className='snackbar-dismiss-btn'
              onClick={() => closeSnackbar(snackbarId)}
            >
              dismiss
            </Button>
          );
          dispatch(
            deleteEventFromDate({
              tripId,
              dayId: day.id,
              dateISO: activeEvent.startDateISO,
              eventId: activeEventId,
              cardId: activeEvent.cardId,
              eventOrder: day.eventOrder,
            })
          );
          enqueueSnackbar(
            `${activeCard.customName ?? activeCard.name} was added back to your ${list?.name ?? 'list'} list`,
            { action }
          );
        };

        if (!over) {
          // Dropped off the calendar grid → remove event
          removeEventAndNotify();
          return;
        }

        if (overType === 'sidebar') {
          // Dragged back to the itinerary sidebar → remove from itinerary
          removeEventAndNotify();
          return;
        }

        if (overType === ItemType.EVENT) {
          const overEvent = over.data.current?.event as TripEvent;
          if (active.id === over.id) {
            return;
          }

          if (activeEvent.startDateISO === overEvent?.startDateISO) {
            // Within-day reorder
            const day = currentTrip.days[activeEvent.startDateISO];
            if (!day?.id || !day.eventOrder) {
              return;
            }
            const oldIndex = day.eventOrder.indexOf(activeEventId);
            const newIndex = day.eventOrder.indexOf(over.id as string);
            if (oldIndex !== -1 && newIndex !== -1 && oldIndex !== newIndex) {
              const newOrder = arrayMove(day.eventOrder, oldIndex, newIndex);
              dispatch(
                tempUpdateEventOrder({
                  dateISO: activeEvent.startDateISO,
                  dragIndex: oldIndex,
                  hoverIndex: newIndex,
                })
              );
              dispatch(
                saveEventOrderForDay({
                  tripId,
                  dayId: day.id,
                  dateISO: activeEvent.startDateISO,
                  eventOrder: newOrder,
                })
              );
            }
            return;
          }

          // Cross-day: dropped directly on an event in a different day
          if (overEvent?.startDateISO && overEvent.startDateISO !== activeEvent.startDateISO) {
            const toDateISO = overEvent.startDateISO;
            const tripDay = currentTrip.days[toDateISO];
            if (!tripDay) {
              return;
            }
            handleEventMoveToDay(activeCard, activeEvent, activeEventId, tripDay, toDateISO);
          }
          return;
        }

        if (overType === 'day') {
          const toDateISO = over.data.current?.dateISO as string;
          const tripDay = over.data.current?.tripDay;
          if (!tripDay || toDateISO === activeEvent.startDateISO) {
            return;
          }
          handleEventMoveToDay(activeCard, activeEvent, activeEventId, tripDay, toDateISO);
        }
        return;
      }
    },
    [currentTrip, tripId, dispatch, enqueueSnackbar, closeSnackbar]
  );

  // ── Confirm / Cancel pending drop ─────────────────────────────────────────
  // When a card is dropped onto a day that already has it (duplicate) or when
  // the venue is closed, we pause and show a confirmation dialog. These
  // callbacks resolve or discard that pending state.

  const handleConfirmDrop = useCallback(() => {
    if (!pendingDrop || !currentTrip || !tripId) {
      return;
    }
    const { type, card, event, dateISO } = pendingDrop;
    const tripDay = currentTrip.days[dateISO];
    if (!tripDay) {
      setPendingDrop(null);
      return;
    }

    // event?.id being present means this confirmation is resolving a cross-day
    // event move (dragging an existing itinerary event). Without it, this is a
    // fresh card being scheduled onto the itinerary for the first time.
    const performDrop = () => {
      if (event?.id) {
        const fromDay = currentTrip.days[event.startDateISO];
        if (fromDay?.id && fromDay.eventOrder) {
          dispatch(
            moveEventToDate({
              tripId,
              fromDayId: fromDay.id,
              toDayId: tripDay.id,
              fromDateISO: event.startDateISO,
              toDateISO: dateISO,
              eventId: event.id,
              fromDayEventOrder: fromDay.eventOrder,
              toDayEventOrder: tripDay.eventOrder,
            })
          );
        }
      } else {
        dispatch(
          addEventToDate({
            tripId,
            dayId: tripDay.id,
            dateISO,
            cardId: card.id,
            eventOrder: tripDay.eventOrder,
          })
        );
      }
    };

    if (type === 'duplicate') {
      // After confirming duplicate, still check if the venue is closed that day
      if (!isOpenOnDate(card, tripDay)) {
        setPendingDrop({ type: 'closed', card, event, dateISO });
      } else {
        setPendingDrop(null);
        performDrop();
      }
    } else {
      // 'closed' — user confirmed they still want to add despite closed status
      setPendingDrop(null);
      performDrop();
    }
  }, [pendingDrop, currentTrip, tripId, dispatch]);

  const handleCancelDrop = useCallback(() => setPendingDrop(null), []);

  return {
    sensors,
    collisionDetectionStrategy,
    activeItem,
    pendingDrop,
    handleDragStart,
    handleDragEnd,
    handleConfirmDrop,
    handleCancelDrop,
  };
}
